import { useId, useLayoutEffect, useRef } from 'react'
import { Info } from 'lucide-react'
import InfoHint from './InfoHint'
import './Form.css'

/**
 * The shared form kit — the layout every admin drawer stacks its rows in, and
 * the two controls that had been re-invented once per feature folder.
 *
 * See Form.css for what each piece is for and why it is here rather than in
 * nine separate stylesheets.
 */

/** The vertical stack. Owns the rhythm between rows; children set nothing. */
export function FormBody({ children, className = '' }) {
  return <div className={`form-body ${className}`.trim()}>{children}</div>
}

/** Two fields on one line — for a pair that is read together, not any two rows. */
export function FormCols({ children, className = '' }) {
  return <div className={`form-cols ${className}`.trim()}>{children}</div>
}

/** A child of FormCols that takes the whole line. */
export function FormSpan({ children, className = '' }) {
  return <div className={`form-span ${className}`.trim()}>{children}</div>
}

/**
 * A run of fields that belong together.
 *
 * NO HEADING BY DEFAULT. It used to carry a title, an accent tick, a rule across the line
 * and an optional spec chip — a band announcing each part of the form. On a drawer of
 * three or four short sections that was more furniture than the fields themselves, and
 * the labels already say what each control is. What the section does by default is space
 * its fields as a group and set them apart from the next one.
 *
 * @param {string} [title]  for a LONG form only — a page of four groups where the reader
 *   needs to know which part they are in. Plain type, no tick, no rule.
 * @param {string} [description]  one line under the title on what the group decides.
 */
export function FormSection({ title, description, children, className = '' }) {
  const id = useId()
  return (
    <section className={`form-section ${className}`.trim()} aria-labelledby={title ? id : undefined}>
      {title && (
        <header className="form-section__head">
          <h3 className="form-section__title" id={id}>
            <bdi>{title}</bdi>
          </h3>
          {description && (
            <p className="form-section__description">
              <bdi>{description}</bdi>
            </p>
          )}
        </header>
      )}
      {children}
    </section>
  )
}

/**
 * The "read this before you fill it in" panel.
 *
 * @param {string} [lead]  a short bold-ish opener before the body — `ملاحظات مهمة`
 * @param {import('react').ElementType} [icon]  defaults to the "i"; pass another
 *   when the note is about a specific thing (a shield for a permissions note)
 * @param {'warning'|'success'|'info'} [tone]  only when the note IS one of those —
 *   a consequence to weigh, a thing confirmed, a fact about the data. Left out, it
 *   wears the identity: the app talking to you.
 */
export function InfoNote({ lead, icon: Icon = Info, tone, children, className = '' }) {
  return (
    <div className={['info-note', tone && `info-note--${tone}`, className].filter(Boolean).join(' ')}>
      <span className="info-note__icon" aria-hidden="true">
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div className="info-note__body">
        {lead && (
          <strong className="info-note__lead">
            <bdi>{lead}</bdi>
          </strong>
        )}{' '}
        {children}
      </div>
    </div>
  )
}

/** The soft container for a decision made of more than one control. */
export function FormWell({ children, className = '' }) {
  return <div className={`form-well ${className}`.trim()}>{children}</div>
}

/** Two settings that belong together, in one well. */
export function SwitchGroup({ children }) {
  return <div className="switch-field-group">{children}</div>
}

/**
 * Multi-line text, in TextField's register.
 *
 * Grows with its content rather than offering a resize grabber — see Form.css.
 * `rows` sets the resting height only.
 */
export function Textarea({
  id,
  value,
  onChange,
  rows = 3,
  dir,
  placeholder,
  invalid,
  describedBy,
}) {
  const ref = useRef(null)

  // Height follows content. `auto` first so the box can also SHRINK when text
  // is deleted — measuring scrollHeight against the current height only ever
  // grows it.
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }, [value])

  return (
    <textarea
      ref={ref}
      id={id}
      dir={dir}
      rows={rows}
      placeholder={placeholder}
      className={`form-textarea${invalid ? ' form-textarea--invalid' : ''}`}
      style={{ minHeight: `${rows * 24 + 26}px` }}
      value={value}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(event) => onChange?.(event.target.value)}
    />
  )
}

/**
 * A setting, as a row: what it is on the reading edge, whether it is on at the
 * far one.
 *
 * @param {string} [hint]  one line on what turning it on actually does
 * @param {string} [info]  the same, behind an "i" beside the label rather than a line
 *   under it — as a FormRow's hint is
 * @param {boolean} [feature]  a page's own on/off switch, standing on the page rather
 *   than in a form: held to the settings column's width instead of the full page
 */
export function SwitchField({ checked, onChange, label, hint, info, id, feature = false }) {
  const generated = useId()
  const inputId = id ?? generated

  return (
    <label
      className={`switch-field${feature ? ' switch-field--feature' : ''}${checked ? ' is-on' : ''}`}
      htmlFor={inputId}
    >
      <span className="switch-field__text">
        <span className="switch-field__label">
          <bdi>{label}</bdi>
          {/* A button inside the label: pressing it opens the tip and leaves the
              switch alone — a label never activates its control from interactive
              content. */}
          {info && <InfoHint content={<bdi>{info}</bdi>} />}
        </span>
        {hint && (
          <span className="switch-field__hint">
            <bdi>{hint}</bdi>
          </span>
        )}
      </span>

      <input
        id={inputId}
        type="checkbox"
        role="switch"
        className="switch-field__input"
        checked={checked}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span className="switch-field__track" aria-hidden="true">
        <span className="switch-field__thumb" />
      </span>
    </label>
  )
}
